import {City} from "./city";
import {PartyType, Person} from "./party";
import {strings} from "../strings/strings";
import {SelectOption} from "../components/inputs/RadioGroup";

export interface Vehicle {
    id: string;
    vehicleType: VehicleType;
    brand: ObjectDetails;
    model: ObjectDetails;
    plateNumber: string;
    vin: string;
    constructionYear: number;
    engineType: EngineType;
    batteryCapacity: number;
    colour: string;
    registerCity: City;
    ownerPartyType: PartyType;
    ownerBirthYear: number;
    taxiUsage: boolean;
    requiredTO: boolean;
    nextToDate: Date;
    coverageTerritory: string;
    engineCapacity: number;
    totalWeight: number;
    ownWeight: number;
    numberSeating: number;
}
export interface ObjectDetails {
    code: string;
    name: string;
}

export const OwnerIsInsuredOptions: SelectOption[] = [
    {value: 'true', label: strings.ownerIsInsured},
    {value: 'false', label: strings.ownerIsNotInsured}
];

export enum EngineType {
    DIESEL = "DIESEL",
    GASOLINE = "GASOLINE",
    ELECTRIC = "ELECTRIC",
    HYBRID = "HYBRID",
    LPG = "LPG",
}

export enum VehicleType {
    A1 = "A1",
    A2 = "A2",
    B1 = "B1",
    B2 = "B2",
    B3_1 = "B3_1",
    B3_2 = "B3_2",
    B4 = "B4",
    B5 = "B5",
    C1 = "C1",
    C2 = "C2",
    D1 = "D1",
    D2 = "D2",
    E = "E",
    F = "F"
}

export enum VehicleGroup {
    B = "B",
    B5 = "B5",
    C = "C",
    D = "D",
    A = "A",
    E = "E"
}

export const VehicleColour: Record<string, string> = {
    "Black": strings.colorBlack,
    "DarkBlue": strings.colorDarkBlue,
    "Green": strings.colorGreen,
    "Beige": strings.colorBeige,
    "Red": strings.colorRed,
    "Violet": strings.colorViolet,
    "Brown": strings.colorBrown,
    "Gray": strings.colorGray,
    "Orange": strings.colorOrange,
    "Yellow": strings.colorYellow,
    "White ": strings.colorWhite
}

export const VehicleGroups: Record<VehicleGroup, VehicleType[]> = {
    "B": [VehicleType.B1, VehicleType.B2, VehicleType.B3_1, VehicleType.B3_2, VehicleType.B4],
    "B5": [VehicleType.B5],
    "C": [VehicleType.C1, VehicleType.C2],
    "D": [VehicleType.D1, VehicleType.D2],
    "A": [VehicleType.A1, VehicleType.A2],
    "E": [VehicleType.E, VehicleType.F]
};

export const VehicleGroupNames: Record<VehicleGroup, string> = {
    B: strings.vehicleGroupB,
    A: strings.vehicleGroupA,
    C: strings.vehicleGroupC,
    B5: strings.vehicleGroupB5,
    E: strings.vehicleGroupE,
    D: strings.vehicleGroupD
};

export const VehicleTypeNames: Record<VehicleType, string> = {
    A1: strings.vehicleTypeA1,
    A2: strings.vehicleTypeA2,
    B1: strings.vehicleTypeB1,
    B2: strings.vehicleTypeB2,
    B3_1: strings.vehicleTypeB3_1,
    B3_2: strings.vehicleTypeB3_2,
    B4: strings.vehicleTypeB4,
    B5: strings.vehicleTypeB5,
    C1: strings.vehicleTypeC1,
    C2: strings.vehicleTypeC2,
    D1: strings.vehicleTypeD1,
    D2: strings.vehicleTypeD2,
    E: strings.vehicleTypeE,
    F: strings.vehicleTypeF
};

export function findVehicleGroup(vehicleType: VehicleType) {
    if (VehicleGroups.B.find(value => value === vehicleType)) {
        return VehicleGroup.B;
    }

    if (VehicleGroups.B5.find(value => value === vehicleType)) {
        return VehicleGroup.B5;
    }

    if (VehicleGroups.C.find(value => value === vehicleType)) {
        return VehicleGroup.C;
    }

    if (VehicleGroups.D.find(value => value === vehicleType)) {
        return VehicleGroup.D;
    }

    if (VehicleGroups.A.find(value => value === vehicleType)) {
        return VehicleGroup.A;
    }

    if (VehicleGroups.E.find(value => value === vehicleType)) {
        return VehicleGroup.E;
    }
}

export function getConstructionYearOptions(min?, max?) {

    let maxYear: number = max ? max : new Date().getFullYear();
    let minYear: number = min ? min : 1970;

    let num: number = 0;
    let opt: string[] = ([]);

    for (num = maxYear; num >= minYear; num--) {
        opt.push(num.toString());
    }

    return opt;
}

export function getOwnerBirthYearOptions() {

    let maxYear: number = new Date().getFullYear() - 18;
    let minYear: number = 1914;

    let num: number = 0;
    let opt: string[] = ([]);

    for (num = maxYear; num >= minYear; num--) {
        opt.push(num.toString());
    }

    return opt;
}

export const mapVehicleFields = (sourceVehicle: Vehicle, targetVehicle: Vehicle): Vehicle => {
    const mapFields = ["id","taxiUsage", "requiredTO", "vehicleType", "brand", "model","plateNumber", "constructionYear",
                        "registerCity", "vin", "ownerPartyType", "ownerBirthYear", "engineType", "batteryCapacity", 
                        "nextToDate", "colour", "engineCapacity", "totalWeight", "ownWeight", "numberSeating"];
    const copy = {...targetVehicle};
    mapFields.forEach(key => {
        if( sourceVehicle[key] !== null ){
            copy[key] = sourceVehicle[key]
        }
    })
    return copy
}

export interface IVehicleIdentificationCodeRequest {
    phoneNumber: string,
    birthDate: Date,
    identificationCode: string,
    verificationCode: string,
    saleVariantCode: string
}

export interface IVehicleIdentificationCodeResponse {
    identificationCodeId: string,
    controlCode: string,
    vehicleAndPrimaryInsuredList: Array<IVehicleAndPrimaryInsured>,
}

export interface IVehicleAndPrimaryInsured {
    vehicle: Vehicle,
    primaryInsured: Person,
}